import {
  CrudFilter,
  CrudSort,
  MetaQuery,
  Pagination
} from 'app/context/data/types';
import {
  createViewSelector,
  updateFilters,
  updatePagination,
  updateSearchKeyword,
  updateSorters
} from 'app/definitions/state-managements/views';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { replacePathParams } from 'app/definitions/helpers/path';
import { useAppDispatch } from 'app/hooks';

type useCustomFilterProps = {
  resource: string;
  syncWithStore?: boolean;
  viewKey?: string;
  searchKeyword?: string;
  pagination?: Pagination;
  sorters?: {
    initial: CrudSort[];
  };
  filters?: {
    initial: CrudFilter[];
  };
  meta?: MetaQuery;
};

export function useCustomFilter(options: useCustomFilterProps) {
  const {
    resource,
    viewKey,
    meta,
    syncWithStore,
    searchKeyword: searchKeywordFromProps,
    pagination: paginationFromProps,
    sorters: sortersFromProps,
    filters: filtersFromProps
  } = options;
  const dispatch = useAppDispatch();
  const reducerKey = replacePathParams(viewKey ? viewKey : resource, meta);
  const preloadState = useMemo(
    () => ({
      pagination: paginationFromProps || {
        current: 1,
        pageSize: 10
      },
      filters: filtersFromProps ? filtersFromProps.initial : [],
      sorters: sortersFromProps ? sortersFromProps.initial : [],
      keywords: ''
    }),
    []
  );

  const viewSelector = useMemo(() => {
    return createViewSelector(reducerKey);
  }, [reducerKey]);

  const connectedToViewState = useSelector<
    any,
    {
      pagination: Pagination;
      filters: CrudFilter[];
      sorters: CrudSort[];
      keywords?: string;
    }
  >(viewSelector as any);

  const [searchKeyword, setSearchKeyword] = useState(searchKeywordFromProps);
  const [pagination, setPagination] = useState(paginationFromProps);
  const [sorter, setSorter] = useState(sortersFromProps?.initial ?? []);
  const [filter, setFilter] = useState(filtersFromProps?.initial ?? []);

  const setPaginationState = (pagination: Pagination) => {
    if (syncWithStore) {
      //   connectedToViewState.updatePagination(pagination);
      dispatch(
        updatePagination({
          key: reducerKey,
          pagination
        })
      );
    } else {
      setPagination(pagination);
    }
  };

  const setSorterState = (sorter: CrudSort[]) => {
    if (syncWithStore) {
      //   connectedToViewState.updateSorters(sorter);
      dispatch(updateSorters({ key: reducerKey, sorters: sorter }));
    } else {
      setSorter(sorter);
    }
  };

  const setFilterState = (filter: CrudFilter[], pagination = null) => {
    if (syncWithStore) {
      //   connectedToViewState.updateFilters(filter);
      dispatch(
        updateFilters({
          key: reducerKey,
          filters: filter,
          pagination
        })
      );
    } else {
      setFilter(filter);
    }
  };

  const setSearchKeywordState = (
    searchKeyword: string,
    pagination?: Pagination
  ) => {
    if (syncWithStore) {
      //   connectedToViewState.updateSearchKeyword(searchKeyword);
      dispatch(
        updateSearchKeyword({
          key: reducerKey,
          keywords: searchKeyword,
          pagination
        })
      );
    } else {
      setSearchKeyword(searchKeyword);
    }
  };

  const syncedData = useMemo(() => {
    if (syncWithStore) {
      return connectedToViewState || preloadState;
    }
    return {
      pagination,
      filters: filter,
      sorters: sorter,
      keywords: searchKeyword
    };
  }, [
    connectedToViewState,
    filter,
    pagination,
    preloadState,
    searchKeyword,
    sorter,
    syncWithStore
  ]);

  return {
    pagination: syncedData.pagination,
    filters: syncedData.filters,
    sorters: syncedData.sorters,
    searchKeyword: syncedData.keywords,
    setPagination: setPaginationState,
    setFilters: setFilterState,
    setSorters: setSorterState,
    setSearchKeyword: setSearchKeywordState
  };
}
